import { inViewport } from '@/helpers';
import { Reward, OfferTypeFilter, CustomerProfile } from '@/models';
import { Routes } from '@/router';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgRewardsExchange',
})
export default class CmgRewardsExchange extends Vue {
  profileWatcher?: () => void;
  shouldShowStickyHeader = false;
  shouldShowFloatingTracker = true;
  selectedOfferType: OfferTypeFilter | null = null;
  
  async mounted(): Promise<void> {
    const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
    if (!isLoggedIn) {
      window.location.replace(`${window.location.origin}${this.$services.config.getRewardsPath()}`);
      return;
    }
    if (!this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile)) {
      this.$router.replace({ name: Routes.Rewards });
      return;
    }
    
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsStore,
      action: AnalyticsEventAction.PageViewed,
      label: AnalyticsEventLabel.RewardStorePageViewed,
    });
    window.addEventListener('scroll', this.onScroll, true);
    
    this.$services.reward.getRewardStoreOfferTypes();
    this.$services.reward.getRewardOffers();
    
    this.profileWatcher = this.$appStore.watch(state => state.customer.profile, this.showExchangeWelcomeModal);
    this.showExchangeWelcomeModal();
  }

  destroyed(): void {
    window.removeEventListener('scroll', this.onScroll, true);

    if (this.profileWatcher) {
      this.profileWatcher();
    }
  }

  showExchangeWelcomeModal(): void {
    if (this.customer && !this.customer.hasSeenRewardsExchOnboarding) {
      this.$services.ui.setModalVisibility('rewardsExchangeWelcome', true);

      // This modal should be displayed only once no matter which device or browser the user is on
      this.customer.hasSeenRewardsExchOnboarding = true;
      this.$services.customer.updateCustomer({ hasSeenRewardsExchOnboarding: true });
    }
  }

  get chevronLeftUrl(): string {
    return this.$appStore.state.cms.images.icons.chevronLeft.default;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get loading(): boolean {
    return !this.isCustomerInitialized || this.$appStore.state.ui.enrollingInRewards;
  }

  get isCustomerInitialized(): boolean {
    return this.$services.customer.isLoggedIn() !== undefined;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get isServiceOnline(): boolean {
    return !this.$appStore.state.reward.unreachable;
  }

  get offlineMessage(): string {
    return `We're having some trouble getting your points and rewards.`;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get sadAvocado(): string {
    return this.$appStore.state.cms.images.feedback.sadAvocado.default;
  }

  get availableOfferTypes() {
    //Determining applicable offer types based on the rewards returned from reward store
    return [...new Set<OfferTypeFilter>(this.$appStore.state.reward.rewards.map(item => item.offerType as OfferTypeFilter))];
  }
  
  get rewards(): Reward[] {  
    return this.$appStore.state.reward.rewards.filter(reward => (this.selectedOfferType ? reward.offerType === this.selectedOfferType : true));
  }

  onClickBack() {
    this.$router.push({ name: Routes.Rewards });
  }

  onScroll(): void {
    if (this.isServiceOnline) {
      this.shouldShowFloatingTracker = !inViewport(this.$refs.rewardTracker as Element);
      this.shouldShowStickyHeader = !inViewport(this.$refs.loyaltyProgressHeader as Element);
    }
  }

  toggleFilter(offerType: OfferTypeFilter) {    
    this.selectedOfferType = (this.selectedOfferType === offerType) ? null : offerType;
    
    if (this.selectedOfferType) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardsStore,
        action: offerType + AnalyticsEventAction.CategoryFiltered,
        label: AnalyticsEventLabel.RewardStoreSpecific + offerType,
      });
    }
  }
}
